'use client'
import { createModal } from '@gluestack-ui/core/modal/creator'
import { ExitAnimationContext } from '@gluestack-ui/core/overlay/creator'
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils'
import { tva, useStyleContext, withStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import { useShortcutScope } from '@tinycld/core/lib/shortcuts/scopes'
import { useRegisterShortcut } from '@tinycld/core/lib/shortcuts/use-register'
import React from 'react'
import { Pressable, ScrollView, View } from 'react-native'
import Animated, { Easing, FadeIn, FadeOut, ZoomIn } from 'react-native-reanimated'
import { withUniwind } from 'uniwind'

const AnimatedPressable = Animated.createAnimatedComponent(Pressable)
const AnimatedView = Animated.createAnimatedComponent(View)
const SCOPE = 'MODAL'

const StyledAnimatedPressable = withUniwind(AnimatedPressable)
const StyledAnimatedView = withUniwind(AnimatedView)

// GlueStack's OverlayAnimatePresence drives its exit handshake via the
// react-native `Animated` API with useNativeDriver:true, whose start()
// callback never fires under react-native-web. That leaves
// animationState stuck at 'exiting' and `setExited(true)` is never
// called — so the Overlay's outer container keeps `display:flex` and
// the dialog node stays mounted in the DOM after `onClose()` runs,
// blocking clicks even though `visible` is false.
//
// This shim renders children as-is (our own enter/exit animations live
// on Backdrop/Content via Reanimated props) AND explicitly flips
// `exited` in the ExitAnimationContext whenever children transition
// to null. That lets the Overlay unmount immediately on close.
const AnimatePresenceShim = React.forwardRef<unknown, { children?: React.ReactNode }>(
    function AnimatePresenceShim({ children }, _ref) {
        const { setExited } = React.useContext(ExitAnimationContext)
        const isPresent = children != null
        React.useEffect(() => {
            setExited(!isPresent)
        }, [isPresent, setExited])
        return <>{children}</>
    }
)

const UIModal = createModal({
    Root: withStyleContext(View, SCOPE),
    Backdrop: StyledAnimatedPressable,
    Content: StyledAnimatedView,
    Body: ScrollView,
    CloseButton: Pressable,
    Footer: View,
    Header: View,
    AnimatePresence: AnimatePresenceShim as React.ComponentType<{ children?: React.ReactNode }>,
})

const modalStyle = tva({
    // z-[1000] lifts the whole modal (backdrop + content) above the mobile
    // sidebar drawer (zIndex 201) and any other in-app overlay, so a dialog
    // opened from a sidebar control — e.g. the label manager from the cog —
    // renders in front of the sidebar rather than behind it.
    base: 'group/modal w-full h-full justify-center items-center web:pointer-events-none z-[1000]',
    variants: {
        size: {
            xs: '',
            sm: '',
            md: '',
            lg: '',
            full: '',
        },
    },
})

const modalBackdropStyle = tva({
    base: 'absolute left-0 top-0 right-0 bottom-0 bg-[#000]/50 web:cursor-default',
})

const modalContentStyle = tva({
    base: 'bg-background rounded-md overflow-hidden border border-border/80 shadow-hard-2 p-6',
    parentVariants: {
        size: {
            xs: 'w-[60%] max-w-[360px]',
            sm: 'w-[70%] max-w-[420px]',
            md: 'w-[80%] max-w-[510px]',
            lg: 'w-[90%] max-w-[640px]',
            full: 'w-full',
        },
    },
})

const modalBodyStyle = tva({
    base: 'mt-2 mb-6',
})

const modalCloseButtonStyle = tva({
    base: 'group/modal-close-button z-10 rounded data-[focus-visible=true]:web:bg-background/90 web:outline-0 cursor-pointer',
})

const modalHeaderStyle = tva({
    base: 'justify-between items-center flex-row',
})

const modalFooterStyle = tva({
    base: 'flex-row justify-end items-center gap-2',
})

type IModalProps = React.ComponentProps<typeof UIModal> &
    VariantProps<typeof modalStyle> & { className?: string }

type IModalBackdropProps = React.ComponentProps<typeof UIModal.Backdrop> &
    VariantProps<typeof modalBackdropStyle> & { className?: string }

type IModalContentProps = React.ComponentProps<typeof UIModal.Content> &
    VariantProps<typeof modalContentStyle> & { className?: string }

type IModalHeaderProps = React.ComponentProps<typeof UIModal.Header> &
    VariantProps<typeof modalHeaderStyle> & { className?: string }

type IModalBodyProps = React.ComponentProps<typeof UIModal.Body> &
    VariantProps<typeof modalBodyStyle> & { className?: string }

type IModalFooterProps = React.ComponentProps<typeof UIModal.Footer> &
    VariantProps<typeof modalFooterStyle> & { className?: string }

type IModalCloseButtonProps = React.ComponentProps<typeof UIModal.CloseButton> &
    VariantProps<typeof modalCloseButtonStyle> & { className?: string }

let modalInstanceCounter = 0

function ModalShortcutBridge({ onClose }: { onClose?: () => void }) {
    useShortcutScope('modal')
    const id = React.useMemo(() => `modal.escape.${++modalInstanceCounter}`, [])
    const shortcut = React.useMemo(
        () =>
            onClose
                ? ({
                      id,
                      keys: 'Escape',
                      scope: 'modal' as const,
                      description: 'Close dialog',
                      allowInInputs: true,
                      run: onClose,
                  } as const)
                : null,
        [id, onClose]
    )
    useRegisterShortcut(shortcut)
    return null
}

const Modal = React.forwardRef<React.ComponentRef<typeof UIModal>, IModalProps>(
    ({ className, size = 'md', ...props }, ref) => (
        <>
            {props.isOpen ? <ModalShortcutBridge onClose={props.onClose} /> : null}
            <UIModal
                ref={ref}
                {...props}
                pointerEvents="box-none"
                className={modalStyle({ size, class: className })}
                context={{ size }}
            />
        </>
    )
)

const ModalBackdrop = React.forwardRef<
    React.ComponentRef<typeof UIModal.Backdrop>,
    IModalBackdropProps
>(function ModalBackdrop({ className, ...props }, ref) {
    return (
        <UIModal.Backdrop
            ref={ref}
            entering={FadeIn.duration(200).easing(Easing.linear)}
            exiting={FadeOut.duration(200).easing(Easing.linear)}
            {...props}
            className={modalBackdropStyle({
                class: className,
            })}
        />
    )
})

const ModalContent = React.forwardRef<
    React.ComponentRef<typeof UIModal.Content>,
    IModalContentProps
>(function ModalContent({ className, size, ...props }, ref) {
    const { size: parentSize } = useStyleContext(SCOPE)

    return (
        <UIModal.Content
            ref={ref}
            entering={ZoomIn.duration(200).withInitialValues({
                transform: [{ scale: 0.9 }],
                opacity: 0,
            })}
            exiting={FadeOut.duration(200)}
            {...props}
            className={modalContentStyle({
                parentVariants: {
                    size: parentSize,
                },
                size,
                class: className,
            })}
            pointerEvents="auto"
        />
    )
})

const ModalHeader = React.forwardRef<React.ComponentRef<typeof UIModal.Header>, IModalHeaderProps>(
    function ModalHeader({ className, ...props }, ref) {
        return (
            <UIModal.Header
                ref={ref}
                {...props}
                className={modalHeaderStyle({
                    class: className,
                })}
            />
        )
    }
)

const ModalBody = React.forwardRef<React.ComponentRef<typeof UIModal.Body>, IModalBodyProps>(
    function ModalBody({ className, ...props }, ref) {
        return (
            <UIModal.Body
                scrollEnabled={false}
                ref={ref}
                {...props}
                className={modalBodyStyle({
                    class: className,
                })}
            />
        )
    }
)

const ModalFooter = React.forwardRef<React.ComponentRef<typeof UIModal.Footer>, IModalFooterProps>(
    function ModalFooter({ className, ...props }, ref) {
        return (
            <UIModal.Footer
                ref={ref}
                {...props}
                className={modalFooterStyle({
                    class: className,
                })}
            />
        )
    }
)

const ModalCloseButton = React.forwardRef<
    React.ComponentRef<typeof UIModal.CloseButton>,
    IModalCloseButtonProps
>(function ModalCloseButton({ className, ...props }, ref) {
    return (
        <UIModal.CloseButton
            ref={ref}
            {...props}
            className={modalCloseButtonStyle({
                class: className,
            })}
        />
    )
})

Modal.displayName = 'Modal'
ModalBackdrop.displayName = 'ModalBackdrop'
ModalContent.displayName = 'ModalContent'
ModalHeader.displayName = 'ModalHeader'
ModalBody.displayName = 'ModalBody'
ModalFooter.displayName = 'ModalFooter'
ModalCloseButton.displayName = 'ModalCloseButton'

export { Modal, ModalBackdrop, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader }
